import { Text } from '@avanta/shared/components/Text';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useFetchPatient } from '@avanta/shared/hooks/useFetchPatient';
import { getPatientName } from '@avanta/shared/utils/appointments';
import { IdCard, Mail, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { EMPTY_VALUE } from 'constants/common';
import { useFetchActivePatientRecord } from 'hooks/useFetchActivePatientRecord';
import { useFetchAppointmentsHistory } from 'hooks/useFetchAppointmentsHistory';
import { getSickLeaveStartDate } from 'pages/PatientProfile/utils';
import { PatientSummarySkeleton } from './PatientSummarySkeleton';
import { getLastSessionDisplay } from './utils';

const SummaryMetaRow = ({
  label,
  value,
  emphasizeValue = false,
}: {
  label: string;
  value: string;
  emphasizeValue?: boolean;
}): React.ReactElement => (
  <div className="flex gap-2 items-center">
    <Text variant="text-xs-medium" className="text-teal-600 w-30">
      {label}
    </Text>
    <Text
      variant={emphasizeValue ? 'text-md-semibold' : 'text-sm-regular'}
      className="text-denim-800"
    >
      {value}
    </Text>
  </div>
);

const InfoLabelRow = ({
  icon: Icon,
  value,
}: {
  icon: React.ElementType;
  value: string;
}): React.ReactElement => (
  <div className="flex items-center gap-2">
    <Icon className="size-4.5 text-teal-600 shrink-0" aria-hidden />
    <Text variant="text-sm-regular" className="text-denim-800">
      {value}
    </Text>
  </div>
);

export const PatientSummary = (): React.ReactElement => {
  const params = useParams();
  const patientId = params?.id ?? '';
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const { data: patientRecord, isLoading: isLoadingPatientRecord } =
    useFetchActivePatientRecord(patientId);
  const { data: patient, isLoading: isLoadingPatient } = useFetchPatient(patientId);
  const { data: appointments = [], isLoading: isLoadingAppointments } =
    useFetchAppointmentsHistory(patientId);
  const isLoading = isLoadingPatient || isLoadingPatientRecord || isLoadingAppointments;
  const lastSession = getLastSessionDisplay(appointments) ?? EMPTY_VALUE;
  const sickLeaveStart = getSickLeaveStartDate(patientRecord);

  if (isLoading) {
    return <PatientSummarySkeleton />;
  }

  return (
    <Card variant="primary" className="am-md:px-8 am-md:py-6 -mt-2">
      <Text variant="heading-lg-medium" className="text-denim-800">
        {getPatientName(patient)}
      </Text>
      <div className="mt-4 flex justify-between gap-12">
        <div className="flex w-full flex-1 max-w-1/2 flex-col gap-3">
          <InfoLabelRow icon={Mail} value={patient?.email || EMPTY_VALUE} />
          <InfoLabelRow icon={IdCard} value={patient?.nif || EMPTY_VALUE} />
          <InfoLabelRow icon={Phone} value={patient?.contactPhone || EMPTY_VALUE} />
        </div>
        <div className="flex w-full flex-1 max-w-1/2 flex-col gap-3">
          <SummaryMetaRow
            label={t('PatientSummary.activeRecord.label')}
            value={patientRecord?.code ?? EMPTY_VALUE}
            emphasizeValue={Boolean(patientRecord?.code)}
          />
          <SummaryMetaRow label={t('PatientSummary.sickLeaveStart.label')} value={sickLeaveStart} />
          <SummaryMetaRow label={t('PatientSummary.lastSession.label')} value={lastSession} />
        </div>
      </div>
    </Card>
  );
};
